import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AppService } from '../app.service';
import { GlobalService } from '../global/services/global.service';
import { WorkspaceService } from '../workspace/workspace.service';
import { generalConfigFromGlobal } from '../shared/workspace-element-context';
import { ScreenDocumentService } from '../screen-video-container/screen-document-upload/screen-document.service';
import { User } from '../screen-video-container/user';
import { UserService } from '../screen-video-container/user.service';
import { ScreenDocsDetails } from './screen-docs-details.model';
import { ScreenVideo } from '../screen-video-container/screen-video-list/screen-video';
import { createGtwAppkitTextEditorConfig } from '../shared/_config/gtw-appkit-text-editor.config';

@Component({
    selector: 'gtw-screen-documents',
    templateUrl: './screen-documents.component.html',
    styleUrls: ['./screen-documents.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ScreenDocumentsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('close-model') closeModal = new EventEmitter<any>();

  data: string = '';
  hdKey: number | undefined;
  durationInSeconds = 5;
  text1: string = ``;
  readonly screenDocsEditorConfig = createGtwAppkitTextEditorConfig('Please add the document');
  public docTitle: string = '';
  public editData: any;
  isEditorShow: boolean = false;
  targetEdit!: HTMLElement | null;
  isLoaded: boolean = false;
  fromHome: boolean = false;
  isShowing: boolean = false;
  user_access: number = 0;
  showTabContent: number = 0;
  isLoading = false;

  @Input() attachmentList: any = [];
  @Input() fromUpload: boolean = false;

  @Output('document-modal-cancel')
  cancelDocModal = new EventEmitter<string>();

  documentData = {} as ScreenDocsDetails;

  private dialogContext: {
    screenKey: number;
    clientKey: number;
    baseUrl: string;
    screenName: string;
  } | null = null;

  private readonly subs = new Subscription();

  constructor(
    public gtwGlobal: GlobalService,
    public gtwWorkspace: WorkspaceService,
    public gtwApp: AppService,
    private userService: UserService,
    private _snackBar: MatSnackBar,
    private screenDocumentService: ScreenDocumentService,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    @Optional() public dialogRef: MatDialogRef<ScreenDocumentsComponent>
  ) {}

  ngOnInit(): void {
    if (this.dialogData?.screen_key != null && this.dialogData.screen_key !== '') {
      // `fromHome` controls the read-only mode (no Create/Edit/Save/Clear/Delete
      // buttons, no editor) — it must be opt-in via `dialogData.fromHome`.
      // Previously this branch unconditionally set fromHome = true for every
      // MatDialog caller (data-filters bar, grid-import toolbar, home page),
      // which silently disabled the legacy `screenDocsController` editing
      // capabilities for the in-screen documentation popup. The legacy
      // `screen-document-tpl.html` opened the CE without the `from-home`
      // attribute, so editing stayed available; we honour the same default
      // here and only flip to read-only when the home page explicitly asks.
      this.fromHome = !!this.dialogData?.fromHome;
      this.dialogContext = {
        screenKey: parseInt(this.dialogData.screen_key + '', 10),
        clientKey: parseInt(String(this.dialogData.client_key), 10),
        baseUrl: this.dialogData.baseUrl || this.getServiceBaseUrl(),
        screenName: this.dialogData.screenName || '',
      };
    }
    this.applyUserFromServices();
    if (!isNaN(this.effectiveClientKey) && !isNaN(this.effectiveScreenKey)) {
      this.init();
    }
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        if (this.fromHome) {
          return;
        }
        if (!isNaN(this.effectiveClientKey) && !isNaN(this.effectiveScreenKey)) {
          this.getDocument();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private activeScreen(): any {
    return this.gtwWorkspace.workspaceFactory?.activeScreen || {};
  }

  private getServiceBaseUrl(): string {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    return String(gc.base_url ?? gc.api ?? '');
  }

  get effectiveScreenKey(): number {
    if (this.dialogContext) {
      return this.dialogContext.screenKey;
    }
    const a = this.activeScreen();
    const raw = a.link ?? a.screenKey ?? a.screen_key;
    return parseInt(String(raw), 10);
  }

  get effectiveClientKey(): number {
    if (this.dialogContext) {
      return this.dialogContext.clientKey;
    }
    const raw =
      this.gtwGlobal.global?.globalParams?.client_key ?? this.gtwApp.USER_SETTINGS?.user?.client_key;
    return parseInt(String(raw), 10);
  }

  get effectiveBaseUrl(): string {
    if (this.dialogContext?.baseUrl) {
      return this.dialogContext.baseUrl;
    }
    return this.getServiceBaseUrl();
  }

  get effectiveScreenName(): string {
    if (this.dialogContext) {
      return this.dialogContext.screenName;
    }
    const a = this.activeScreen();
    return a.screenName || a.screen_name || a.label || '';
  }

  private applyUserFromServices(): void {
    const u = this.gtwApp.USER_SETTINGS?.user as User | undefined;
    if (u) {
      this.userService.setUser(u);
      const lvl = (u as any).system_access_level ?? (u as any).client?.user_type_key;
      this.user_access = parseInt(lvl != null ? String(lvl) : '0', 10) || 0;
    }
  }

  private openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: this.durationInSeconds * 1000,
    });
  }

  init(): void {
    this.getDocument();
  }

  openTab(tab: number) {
    this.showTabContent = tab;
  }

  htmlEncode(string: string) {
    return string
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/'/g, '&#39;')
      .replace(/"/g, '&#34;')
      .replace(/\//, '&#x2F;');
  }

  htmlDecode(input: string) {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent;
  }

  getDocument() {
    this.isEditorShow = false;
    this.isLoading = true;
    this.screenDocumentService
      .getAllDocumentsList(this.effectiveBaseUrl, this.effectiveClientKey, this.effectiveScreenKey).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          if (res.callSuccess == 1 || parseInt(res.callSuccess) == 1) {
            this.attachmentList = res.data;
            if (this.attachmentList && this.attachmentList.length > 0) {
              const decodedHtml = this.htmlDecode(this.attachmentList[0]['hd_body']);
              this.documentData['hd_body'] = decodedHtml || '';
              this.documentData['hd_key'] = this.attachmentList[0]['hd_key'];
            } else {
              this.documentData['hd_body'] = '';
              this.isEditorShow = false;
            }
            this.documentData['hd_msg_title'] = this.effectiveScreenName;
            this.isLoading = false;
          } else {
            this.isLoading = false;
          }
        },
        () => {}
      );

    this.isLoaded = true;
  }

  onChange(newValue: any) {
    this.text1 = newValue;
    this.editData = this.text1;
  }

  onChange1(newValue: any) {
    this.text1 = newValue;
    this.editData = this.text1;
  }

  saveDocument(_event: any, _text1: any) {
    const rawHtml = this.text1 ?? '';
    this.documentData['hd_body'] = this.htmlEncode(rawHtml);
    this.docTitle = this.effectiveScreenName;
    this.screenDocumentService
      .uploadDocument(
        this.effectiveBaseUrl,
        this.effectiveClientKey,
        this.effectiveScreenKey,
        this.documentData.hd_msg_title,
        this.documentData.hd_body,
        this.documentData.hd_key
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          if (res.callSuccess == 1 || parseInt(res.callSuccess) == 1) {
            this.docTitle = '';
            this.text1 = '';
            this.openSnackBar('Document Saved Successfully !!');
            this.getDocument();
            this.isEditorShow = false;
          } else {
            this.openSnackBar(res.errorMessage);
          }
        },
        () => {}
      );
  }

  clearContent(_event: any) {
    this.text1 = '';
  }

  editMode(_event: any) {
    this.isEditorShow = true;
    if (this.attachmentList && this.attachmentList.length > 0) {
      this.documentData['hd_body'] = '';
      this.text1 = this.attachmentList[0]['hd_body'];
    }
  }

  deleteDoc(): void {
    const hdKey = this.documentData['hd_key'] ? this.documentData['hd_key'] : null;
    if (hdKey) {
      this.screenDocumentService
        .deleteDocumentById(this.effectiveBaseUrl, this.effectiveClientKey, this.effectiveScreenKey, hdKey).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          (res) => {
            if (res.callSuccess == 1 || parseInt(res.callSuccess) == 1) {
              this.openSnackBar('Document Deleted Successfully !!');
              this.getDocument();
            } else {
              this.openSnackBar(res.errorMessage);
            }
          },
          () => {}
        );
    }
  }

  cancel() {
    this.isShowing = true;
    // Close mechanism is decoupled from the read-only/edit mode. Whenever the
    // component is hosted in a MatDialog (any caller passing the dialogRef),
    // close it directly. The CE-mode events still fire so legacy AngularJS
    // hosts that listen to `document-modal-cancel` / `close-model` keep working
    // for backward compatibility (gtw-screen-documents-ce in legacy bundles).
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.closeModal.emit('true');
    this.cancelDocModal.emit('document-modal-cancel');
  }

  isValidUser(sso: string) {
    if (this.userService.isValidUser(['admin']) && this.userService.getUser()?.sso == sso) {
      return true;
    }
    if (this.userService.isValidUser(['super admin', 'developer'])) {
      return true;
    }
    return false;
  }
}
